import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ClipboardList, Search, X } from "lucide-react";
import { getCustomerOptionLabel } from "../../utils/customerDisplay";

/**
 * CreateRequestModal — Diálogo de creación de una solicitud de cliente.
 *
 * Extraído 1:1 desde `pages/RequestsPage.js`. Incluye buscador de cliente con dropdown de
 * autocompletado, selector de servicio relacionado, título, descripción, prioridad y
 * asignación inicial de técnico.
 *
 * @param {boolean}  props.createModalOpen        Apertura del diálogo.
 * @param {Function} props.setCreateModalOpen      Setter del flag de apertura.
 * @param {string}   props.customerSearch          Texto del buscador de cliente.
 * @param {Function} props.setCustomerSearch       Setter del buscador.
 * @param {boolean}  props.showCustomerDropdown    Visibilidad del dropdown de clientes.
 * @param {Function} props.setShowCustomerDropdown Setter de la visibilidad.
 * @param {Object}   props.newRequest              Estado del formulario de la nueva solicitud.
 * @param {Function} props.setNewRequest           Setter del formulario.
 * @param {Array}    props.services                Servicios del cliente seleccionado.
 * @param {Function} props.setServices             Setter de los servicios (reset al limpiar cliente).
 * @param {Array}    props.filteredCustomers       Clientes filtrados por el buscador.
 * @param {Array}    props.supportUsers            Usuarios asignables (técnicos/soporte).
 * @param {Function} props.handleCreateRequest     Handler de creación de la solicitud.
 */
export const CreateRequestModal = ({
  createModalOpen,
  setCreateModalOpen,
  customerSearch,
  setCustomerSearch,
  showCustomerDropdown,
  setShowCustomerDropdown,
  newRequest,
  setNewRequest,
  services,
  setServices,
  filteredCustomers,
  supportUsers,
  handleCreateRequest,
}) => {
  return (
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[#03BEE4]" />
              Nueva Solicitud
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Label className="text-slate-300">Cliente *</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Buscar por nombre, DNI o código..."
                  className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white"
                />
                {newRequest.customer_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 text-slate-400 hover:text-white"
                    onClick={() => {
                      setNewRequest({ ...newRequest, customer_id: "", service_id: "" });
                      setCustomerSearch("");
                      setServices([]);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {showCustomerDropdown && filteredCustomers.length > 0 && !newRequest.customer_id && (
                <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto bg-[#0A0A35] border border-[#1E1E5F] rounded-lg shadow-lg">
                  {filteredCustomers.slice(0, 10).map(c => (
                    <div
                      key={c.id}
                      className="px-3 py-2 cursor-pointer hover:bg-[#1B1B4B] border-b border-[#1E1E5F] last:border-0"
                      onClick={() => {
                        setNewRequest({ ...newRequest, customer_id: c.id, service_id: "" });
                        setCustomerSearch(getCustomerOptionLabel(c));
                        setShowCustomerDropdown(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium text-sm">
                            {getCustomerOptionLabel(c)}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {c.dni_nie_cif || c.dni_nie || c.cif || ""}
                          </p>
                        </div>
                        <Badge className="bg-[#1B1B4B] text-slate-300 text-xs">
                          {c.customer_type === "EMPRESA" ? "Empresa" : "Residencial"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {newRequest.customer_id && services.length > 0 && (
              <div>
                <Label className="text-slate-300">Servicio Relacionado</Label>
                <Select
                  value={newRequest.service_id}
                  onValueChange={(value) => setNewRequest({ ...newRequest, service_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                    <SelectValue placeholder="Seleccionar servicio (opcional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id} className="text-white">
                        {s.nombre_servicio}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-slate-300">Título *</Label>
              <Input
                value={newRequest.title}
                onChange={(e) => setNewRequest({ ...newRequest, title: e.target.value })}
                placeholder="Resumen breve de la solicitud"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300">Descripción *</Label>
              <Textarea
                value={newRequest.description}
                onChange={(e) => setNewRequest({ ...newRequest, description: e.target.value })}
                placeholder="Descripción detallada de la solicitud"
                className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white h-24"
              />
            </div>

            <div>
              <Label className="text-slate-300">Prioridad</Label>
              <Select
                value={newRequest.priority}
                onValueChange={(value) => setNewRequest({ ...newRequest, priority: value })}
              >
                <SelectTrigger className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="BAJA" className="text-slate-300">Baja</SelectItem>
                  <SelectItem value="MEDIA" className="text-blue-400">Media</SelectItem>
                  <SelectItem value="ALTA" className="text-orange-400">Alta</SelectItem>
                  <SelectItem value="CRITICA" className="text-red-400">Crítica</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assign at creation time. Same user source as the detail
                assignment selector (TECNICO + SOPORTE, fallback all active). */}
            <div data-testid="create-request-assign-block">
              <Label className="text-slate-300">Asignar a</Label>
              <Select
                value={newRequest.assigned_to_id || "none"}
                onValueChange={(value) => setNewRequest({
                  ...newRequest,
                  assigned_to_id: value === "none" ? "" : value,
                })}
              >
                <SelectTrigger
                  className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                  data-testid="create-request-assign-trigger"
                >
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                  <SelectItem value="none" className="text-slate-400">Sin asignar</SelectItem>
                  {supportUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-white">
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setCreateModalOpen(false)}
              className="border-[#1E1E5F] text-slate-300"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleCreateRequest}
              className="bg-[#42E695] text-[#04042C]"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Crear Solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};
